import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Settings,
  CreditCard,
  MessageCircle,
  BarChart3,
  Calendar,
  Edit3,
  Mail,
  Phone,
  MapPin,
  FileText,
  Check,
  Download,
  ArrowUpRight,
  Utensils,
  Shield,
  Bell,
  Monitor,
  Building2,
  Menu,
  LogOut,
  Smartphone,
  X,
} from 'lucide-react';
import BranchesPanel from './BranchesPanel';
import {
  PageHeader, Card, StatCard, Badge, Alert,
  Btn, FieldLabel, Input, Textarea, Select, dash, cn,
  TrialBanner, PricingFootnote, PricingPlanCard, StepIndicator, QuickActionCard, Modal, CloudComingSoonAlert,
} from './dashboard/DashboardUI';
import jsPDF from 'jspdf';
import { jwtDecode } from 'jwt-decode';
import { isTrialActive, isTrialRegistration, getTrialDaysRemaining, TRIAL_DAYS } from '../utils/trial';
import {
  getPortalPricingPlans,
  PRICING_FOOTNOTE,
  RENEWAL_PRICE,
} from '../utils/pricing';
import {
  branchHasActiveCounterPc,
  branchHasCounterPcSlotUsed,
  canConnectCounterPc,
  getBranchesAvailableForCounterPc,
} from '../utils/devices';

const DashboardLayout = ({ onLogout }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'overview');
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [registrationStep, setRegistrationStep] = useState('serial'); // 'serial' or 'license'
  const [deviceSerial, setDeviceSerial] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceUsername, setDeviceUsername] = useState('');
  const [devicePassword, setDevicePassword] = useState('');
  const [connectingCounterPc, setConnectingCounterPc] = useState(false);
  const [disconnectingDeviceId, setDisconnectingDeviceId] = useState(null);

  const [copiedKey, setCopiedKey] = useState('');
  const [generatedLicenseKey, setGeneratedLicenseKey] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchError, setBranchError] = useState(null);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [userId, setUserId] = useState(null);
  const [restaurantForm, setRestaurantForm] = useState({
    name: '', email: '', phone: '', gst: '', address: ''
  });
  const [restaurantSaveMsg, setRestaurantSaveMsg] = useState('');
  const [passwordChangeMsg, setPasswordChangeMsg] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState({
    mac: '', macIntel: '', win: '', android: '', ios: '',
  });
  const [downloadingId, setDownloadingId] = useState('');

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'branches', label: 'Branches', icon: Building2 },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'billing', label: 'Billing & Invoices', icon: CreditCard },
    { id: 'contact', label: 'Contact Us', icon: MessageCircle },
  ];

  const copyLicenseKey = async (licenseKey, deviceId) => {
    try {
      await navigator.clipboard.writeText(licenseKey);
      setCopiedKey(deviceId);
      setTimeout(() => setCopiedKey(''), 2000);
    } catch (err) {
      console.error('Failed to copy license key:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch branches');
      const data = await res.json();
      setBranches(data);
      if (!selectedBranchId && data.length > 0) {
        const defaultBranch = data.find((b) => b.isDefault) || data[0];
        setSelectedBranchId(defaultBranch._id);
      }
      setBranchError(null);
      return data;
    } catch (err) {
      setBranchError(err.message);
      return [];
    }
  };

  const fetchDownloadUrls = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/downloads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.platforms) setDownloadUrls(data.platforms);
    } catch {
      // Downloads optional until ImageKit URLs are configured on the server
    }
  };

  const fetchDevices = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/devices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch devices');
      const data = await res.json();
      setDevices(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/invoices`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch invoices');
      const data = await res.json();
      setInvoices(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const registerDevice = async (licenseKey, amount) => {
    try {
      if (!licenseKey) {
        setError('License key generation failed');
        return false;
      }

      const token = localStorage.getItem('token');
      if (!token) throw new Error('Not authenticated');

      const onTrial = isTrialRegistration(restaurant, devices.length);
      const expiry = onTrial && restaurant?.trialEndsAt
        ? new Date(restaurant.trialEndsAt)
        : (() => {
            const d = new Date();
            d.setFullYear(d.getFullYear() + 1);
            return d;
          })();

      const res = await fetch(`${process.env.REACT_APP_API_URL}/devices/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          token: licenseKey,
          expiry,
          username: deviceUsername,
          password: devicePassword,
          status: 'active',
          machine_id: deviceSerial,
          branchId: selectedBranchId,
          name: deviceName,
          amount,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to connect counter PC');
      }
      await fetchDevices();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    const fetchRestaurant = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Not authenticated');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/restaurants/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to fetch');
        const data = await res.json();
        setRestaurant(data || null);
        if (data) {
          setRestaurantForm({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            gst: data.gst || '',
            address: data.address || ''
          });
        }
      } catch (err) {
        setError('Could not load restaurant info');
      } finally {
        setLoading(false);
      }
    };
    // Parse userId from JWT
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.id || decoded._id || decoded.userId);
      } catch {}
    }
    fetchRestaurant();
    fetchBranches();
    fetchDevices();
    fetchInvoices();
    fetchDownloadUrls();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && sidebarItems.some((item) => item.id === tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
    setSidebarOpen(false);
  };

  const openRegistration = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [branchRes, deviceRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/branches`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/devices`, { headers }),
      ]);
      if (branchRes.ok) {
        const branchData = await branchRes.json();
        setBranches(branchData);
        const deviceData = deviceRes.ok ? await deviceRes.json() : devices;
        if (deviceRes.ok) setDevices(deviceData);
        const availableBranches = getBranchesAvailableForCounterPc(branchData, deviceData);
        if (availableBranches.length > 0) {
          setSelectedBranchId(availableBranches[0]._id);
        }
      } else {
        await fetchBranches();
        await fetchDevices();
      }
    } catch {
      await fetchBranches();
      await fetchDevices();
    }

    setShowRegistrationModal(true);
  };

  const handleRevokeCounterPc = async (deviceId) => {
    if (
      !window.confirm(
        'Revoke this counter PC\'s access?\n\nThis branch cannot register another counter PC afterward. Add a new branch (₹1,999/year) for another shop location.',
      )
    ) {
      return;
    }
    setDisconnectingDeviceId(deviceId);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/devices/${deviceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to revoke counter PC access');
      await fetchDevices();
    } catch (err) {
      setError(err.message);
    } finally {
      setDisconnectingDeviceId(null);
    }
  };

  const trialActive = isTrialActive(restaurant);
  const trialDaysLeft = getTrialDaysRemaining(restaurant);
  const trialFirstDevice = isTrialRegistration(restaurant, devices.length);
  const canAddCounterPc = canConnectCounterPc(branches, devices);

  const restaurantInitial = (restaurant?.name || 'R').charAt(0).toUpperCase();
  const activeNavLabel = sidebarItems.find((i) => i.id === activeTab)?.label || 'Overview';

  const renderSidebar = (wrapperClass, showClose = false) => (
    <div className={wrapperClass}>
      <div className="p-5 flex flex-col h-full">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="bg-white/95 p-1.5 rounded-xl shadow-md flex items-center justify-center">
              <img src="/logo.png" alt="HomeBillingPOS Logo" className="h-9 w-auto object-contain" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-tight">HomeBillingPOS</h2>
              <p className="text-xs text-slate-400">Offline-First Point of Sale</p>
            </div>
          </div>
          {showClose && (
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className={cn(dash.nested, 'p-3 mb-6')}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center text-white font-semibold text-sm shadow-lg shadow-indigo-600/20">
              {restaurantInitial}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white font-medium text-sm truncate">{restaurant?.name || 'Loading...'}</p>
              <p className="text-slate-500 text-xs truncate">{restaurant?.email}</p>
              {trialActive && (
                <Badge variant="info" className="mt-2">
                  Trial · {trialDaysLeft}d left
                </Badge>
              )}
            </div>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {sidebarItems.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleTabChange(item.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all',
                  active ? dash.navActive : dash.navIdle,
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                <span className="font-medium text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <Btn
          variant="danger"
          className="w-full mt-6"
          onClick={() => {
            localStorage.removeItem('token');
            if (onLogout) onLogout();
          }}
        >
          <LogOut className="w-4 h-4" />
          Logout
        </Btn>
      </div>
    </div>
  );

  const activeBranchCount = branches.filter((b) => b.status === 'active').length;
  const activeDeviceCount = devices.filter(
    (d) => d.status === 'active' || d.status === 'Active',
  ).length;
  const registeredLabel = restaurant?.registrationDate
    ? new Date(restaurant.registrationDate).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  /** Open on iPad Safari → one tap installs via Apple itms-services + hosted manifest */
  const IOS_INSTALL_PAGE = 'https://www.homebillingpos.com/downloads/ios/install.html';
  const IOS_OTA_MANIFEST =
    'https://www.homebillingpos.com/downloads/ios/manifest.plist';

  const downloadPlatforms = [
    { id: 'mac', label: 'macOS (Apple Silicon)', hint: 'M1 / M2 / M3 / M4 counter PC (.dmg)' },
    { id: 'macIntel', label: 'macOS (Intel)', hint: 'Intel Mac counter PC (.dmg)' },
    { id: 'win', label: 'Windows', hint: 'Counter PC installer (.exe)' },
    { id: 'android', label: 'Android', hint: 'Staff phone / tablet (.apk) — open file to install' },
    {
      id: 'ios',
      label: 'iOS / iPad — Install',
      hint: 'Open on iPad Safari → tap Install (one-tap install)',
    },
  ];

  /**
   * Direct download for desktop/Android; iOS uses Apple OTA install (itms-services).
   */
  const startAppDownload = (platformId, remoteUrl) => {
    setError(null);

    // iOS: one-tap install on device (must use Safari)
    if (platformId === 'ios') {
      const isIOS =
        /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS) {
        const ota = `itms-services://?action=download-manifest&url=${encodeURIComponent(
          IOS_OTA_MANIFEST,
        )}`;
        window.location.href = ota;
      } else {
        // Desktop/Android browser: open install page so user can open it on iPad
        window.open(IOS_INSTALL_PAGE, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    if (!remoteUrl || downloadingId) return;
    setDownloadingId(platformId);
    try {
      let href = remoteUrl;
      try {
        const u = new URL(remoteUrl);
        u.search = '';
        href = u.toString();
      } catch {
        /* keep original */
      }

      // Direct navigation triggers browser download of the file
      const a = document.createElement('a');
      a.href = href;
      a.rel = 'noopener';
      a.target = '_blank';
      // filename hint (ignored cross-origin but still starts download)
      const name = href.split('/').pop() || 'download';
      a.setAttribute('download', name);
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => setDownloadingId(''), 1500);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Download failed. Try again.');
      setDownloadingId('');
    }
  };

  const renderOverview = () => (
    <div className={dash.page}>
      {trialActive && trialFirstDevice && (
        <TrialBanner
          trialDaysLeft={trialDaysLeft}
          trialEndsAt={restaurant?.trialEndsAt}
        />
      )}
      <PageHeader
        overline="Owner portal"
        title={`Welcome back${restaurant?.name ? `, ${restaurant.name}` : ''}`}
        subtitle={trialActive
          ? `Your 7-day trial is active — connect one counter PC free for ${trialDaysLeft} more day${trialDaysLeft === 1 ? '' : 's'}. After trial: ₹${RENEWAL_PRICE}/year per branch.`
          : 'One counter PC per branch. Add a branch (₹1,999/year) for each new shop location. Mobile on your LAN is included.'}
      />

      <div className="grid w-full min-w-0 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        <QuickActionCard
          icon={Building2}
          title="Manage branches"
          description="Each location gets its own sync key. Standard plan includes one branch."
          actionLabel="View branches"
          onClick={() => handleTabChange('branches')}
          accent="emerald"
        />
        <QuickActionCard
          icon={CreditCard}
          title="Plans & billing"
          description={PRICING_FOOTNOTE}
          actionLabel="View pricing"
          onClick={() => handleTabChange('billing')}
          accent="sky"
        />
        <QuickActionCard
          icon={Download}
          title="Download POS app"
          description="Desktop POS is free to download. Connect it to your cloud account to activate."
          actionLabel="Get the app"
          onClick={() => document.getElementById('download-apps')?.scrollIntoView({ behavior: 'smooth' })}
          accent="indigo"
        />
      </div>

      <div className={dash.gridStats}>
        <StatCard icon={Building2} label="Branches" value={activeBranchCount} hint={`${restaurant?.plan || 'standard'} plan`} accent="indigo" />
        <StatCard icon={Monitor} label="Counter PCs" value={devices.length} hint={`${activeDeviceCount} active`} accent="emerald" />
        <StatCard
          icon={Shield}
          label="Plan"
          value={trialActive ? 'Trial' : (restaurant?.plan || '—')}
          hint={trialActive ? `${trialDaysLeft}d left · branch trial` : `₹${RENEWAL_PRICE}/branch/year`}
          accent="sky"
        />
        <StatCard icon={Calendar} label="Member since" value={registeredLabel} accent="amber" />
      </div>

      <div className={dash.grid3}>
        <Card className="xl:col-span-1">
          <div className="flex items-center gap-3 mb-5 min-w-0">
            <div className={dash.iconBox}>
              <Utensils className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-white truncate">{restaurant?.name || 'Your restaurant'}</h3>
              <Badge variant="success">{restaurant?.status || 'active'}</Badge>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            {[
              { icon: Shield, text: <>Plan: <span className="text-indigo-400 font-medium capitalize">{restaurant?.plan}</span></> },
              { icon: Calendar, text: <>Registered {registeredLabel}</> },
              { icon: Mail, text: restaurant?.email },
              { icon: Phone, text: restaurant?.phone },
              { icon: MapPin, text: restaurant?.address },
            ].map((row, i) => row.text ? (
              <div key={i} className="flex items-start gap-3 text-slate-300">
                <row.icon className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <span className="break-words">{row.text}</span>
              </div>
            ) : null)}
          </div>
        </Card>

        <Card className="xl:col-span-2">
          <div className="flex flex-col gap-4 min-w-0">
            <div className="flex items-start sm:items-center justify-between gap-3 min-w-0">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-white">Branches</h3>
                <p className="text-slate-500 text-xs mt-1">
                  Each location has its own sync key for the counter PC on that shop LAN.
                </p>
              </div>
              <Btn variant="ghost" onClick={() => handleTabChange('branches')} className="shrink-0">
                Manage →
              </Btn>
            </div>

            {branchError && <Alert variant="error">{branchError}</Alert>}
            <CloudComingSoonAlert compact />

            {branches.length === 0 ? (
              <p className="text-slate-400 text-sm py-2">
                No branches yet. Your default branch is created on signup.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 min-w-0">
                {branches.slice(0, 4).map((branch) => (
                  <div
                    key={branch._id}
                    className={cn(dash.nested, 'p-4 sm:p-5 flex flex-col gap-3')}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-indigo-500/15 flex items-center justify-center shrink-0">
                        <Building2 className="w-5 h-5 text-indigo-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 gap-y-1.5">
                          <p className="text-white font-semibold truncate min-w-0">
                            {branch.name}
                          </p>
                          {branch.isDefault && <Badge variant="info">Default</Badge>}
                        </div>
                        {(branch.city || branch.code) && (
                          <p className="text-slate-500 text-xs mt-1 truncate">
                            {[branch.code, branch.city].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="rounded-lg bg-slate-900/80 border border-slate-800/80 px-3 py-2.5">
                      <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                        Branch sync key
                      </p>
                      <p className="text-emerald-400/90 text-xs sm:text-sm break-all font-mono leading-relaxed">
                        {branch.syncLicenseKey}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      </div>

      <div className={dash.grid3}>
        <Card className="xl:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Connected counter PCs</h3>
          {devices.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-4">
                <Monitor className="w-7 h-7 text-indigo-400" />
              </div>
              <p className="text-white font-medium mb-1">No counter PCs connected yet</p>
              <p className="text-slate-400 text-sm max-w-sm mx-auto">
                {trialFirstDevice
                  ? 'Start your 7-day free trial — use Connect counter PC in the top bar.'
                  : 'Each branch allows one counter PC registration, once only. Add a branch for each new location.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {devices.map((device) => (
                <div key={device._id || device.id} className={cn(dash.nested, 'p-4 flex flex-col sm:flex-row sm:items-center gap-4')}>
                  <div className="w-12 h-12 rounded-xl bg-emerald-600/20 flex items-center justify-center shrink-0">
                    <Monitor className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold">{device.name || 'Counter PC'}</p>
                    <p className="text-slate-400 text-sm mt-0.5">
                      {device.branchId?.name || 'Main'} · {device.serial || device.serialNumber || device.machine_id}
                    </p>
                    <p className="text-slate-500 text-xs mt-1">
                      Connected {device.createdAt ? new Date(device.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                    </p>
                  </div>
                  <Badge variant={device.status === 'active' || device.status === 'Active' ? 'success' : 'warning'}>
                    {device.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card id="download-apps">
          <h3 className="text-lg font-semibold text-white mb-2">Get the app</h3>
          <p className="text-slate-400 text-sm mb-4">Desktop POS is free to download. Connect it to your cloud account to activate.</p>
          <div className="space-y-2">
            {downloadPlatforms.map((p) => {
              const url = downloadUrls[p.id];
              const busy = downloadingId === p.id;
              return url ? (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => startAppDownload(p.id, url)}
                  disabled={!!downloadingId}
                  className="flex items-center justify-between gap-3 w-full rounded-xl border border-slate-700/80 bg-slate-800/50 px-4 py-3 text-sm text-white hover:bg-slate-800 hover:border-indigo-500/40 transition-colors text-left disabled:opacity-60"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Download className="w-4 h-4 shrink-0 text-indigo-400" />
                    <span>
                      <span className="font-medium">{p.label}</span>
                      <span className="block text-xs text-slate-500">
                        {busy ? 'Downloading… stay on this page' : p.hint}
                      </span>
                    </span>
                  </span>
                  <Download className="w-4 h-4 shrink-0 text-slate-500" />
                </button>
              ) : (
                <Btn key={p.id} variant="secondary" className="w-full text-sm opacity-60 cursor-not-allowed" disabled>
                  <Download className="w-4 h-4" />
                  {p.label}
                  <span className="text-xs text-slate-500 ml-1">(coming soon)</span>
                </Btn>
              );
            })}
          </div>
          <div className={cn(dash.nested, 'mt-4 p-3 flex gap-3')}>
            <Smartphone className="w-5 h-5 text-indigo-400 shrink-0" />
            <div className="text-slate-400 text-xs leading-relaxed space-y-2">
              <p>
                Mobile on the same branch Wi‑Fi syncs free — no extra license needed.
              </p>
              <p className="text-slate-300 font-medium">iPad / iPhone — one-tap install</p>
              <p>
                On the <strong className="text-slate-200">iPad, open this portal in Safari</strong>,
                then tap <strong className="text-slate-200">iOS / iPad — Install</strong>. Apple will
                ask to install HomePOS. (Do not open the .ipa from Files.)
              </p>
              <p>
                Direct install page:{' '}
                <a
                  className="text-indigo-400 underline break-all"
                  href={IOS_INSTALL_PAGE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {IOS_INSTALL_PAGE}
                </a>
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Use Safari only (Chrome/Firefox cannot start iOS installs).</li>
                <li>
                  After install: Settings → General → VPN &amp; Device Management → Trust developer.
                </li>
                <li>
                  If install fails, this iPad’s UDID must be on the Ad Hoc profile used to sign the
                  app.
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      <PricingFootnote />
    </div>
  );

  const renderSettings = () => (
    <div className={dash.page}>
      <PageHeader
        overline="Account"
        title="Settings"
        subtitle="Manage counter PCs, restaurant details, and your login password."
      />

      <Card>
        <h3 className="text-lg font-semibold text-white mb-6">Counter PCs</h3>

        {devices.length === 0 ? (
          <p className="text-slate-400 text-sm">No counter PCs connected yet. Connect a desktop to receive cloud activation credentials.</p>
        ) : (
          <div className="space-y-4">
            {devices.map((device) => (
              <div key={device._id || device.id} className={cn(dash.nested, 'p-4')}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div className="flex items-center gap-4">
                    <div className={dash.iconBox}>
                      <Monitor className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{device.name}</h4>
                      <p className="text-slate-400 text-sm">{device.serial || device.serialNumber || device.machine_id}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={device.status === 'active' || device.status === 'Active' ? 'success' : 'danger'}>
                      {device.status}
                    </Badge>
                    <p className="text-slate-500 text-xs mt-2">Expires: {device.expiry || device.expiryDate || '—'}</p>
                  </div>
                </div>

                <div className={cn(dash.nested, 'p-3 mb-3')}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-500 text-xs mb-1">Activation key</p>
                      <p className="text-white text-sm font-mono break-all">{device.token || device.licenseKey}</p>
                    </div>
                    <Btn
                      className="text-sm py-2"
                      onClick={() => copyLicenseKey(device.token || device.licenseKey, device._id || device.id)}
                    >
                      {copiedKey === (device._id || device.id) ? <><Check className="w-4 h-4" /> Copied</> : <><FileText className="w-4 h-4" /> Copy</>}
                    </Btn>
                  </div>
                </div>

                <div className={cn(dash.nested, 'p-3')}>
                  <p className="text-slate-500 text-xs mb-3">Device credentials</p>
                  <div className="space-y-3">
                    {['username', 'password'].map((field) => (
                      <div key={field} className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-slate-500 text-xs capitalize">{field}</p>
                          <p className="text-white text-sm font-mono truncate">{device[field]}</p>
                        </div>
                        <Btn
                          variant="secondary"
                          className="text-xs py-1.5 px-2"
                          onClick={() => copyLicenseKey(device[field], `${device._id || device.id}_${field}`)}
                        >
                          {copiedKey === `${device._id || device.id}_${field}` ? 'Copied' : 'Copy'}
                        </Btn>
                      </div>
                    ))}
                  </div>
                </div>

                {(device.status === 'active' || device.status === 'Active') && (
                  <div className="mt-4 space-y-2">
                    <p className="text-slate-500 text-xs">
                      Revoking access blocks this PC immediately. This branch&apos;s one-time counter PC slot stays used — you cannot generate another key here.
                    </p>
                    <div className="flex justify-end">
                      <Btn
                        variant="danger"
                        className="text-sm"
                        disabled={disconnectingDeviceId === (device._id || device.id)}
                        onClick={() => handleRevokeCounterPc(device._id || device.id)}
                      >
                        {disconnectingDeviceId === (device._id || device.id) ? 'Revoking…' : 'Revoke access'}
                      </Btn>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-white mb-6">Restaurant information</h3>
        {restaurantSaveMsg && (
          <div className="mb-4">
            <Alert variant={restaurantSaveMsg.includes('success') ? 'success' : 'error'}>{restaurantSaveMsg}</Alert>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 min-w-0">
          <div className="min-w-0">
            <FieldLabel>Restaurant name</FieldLabel>
            <Input icon={Utensils} type="text" name="name" value={restaurantForm.name} onChange={handleRestaurantFormChange} />
          </div>
          <div>
            <FieldLabel>Email address</FieldLabel>
            <Input icon={Mail} type="email" name="email" value={restaurantForm.email} onChange={handleRestaurantFormChange} />
          </div>
          <div>
            <FieldLabel>Phone number</FieldLabel>
            <Input icon={Phone} type="tel" name="phone" value={restaurantForm.phone} onChange={handleRestaurantFormChange} />
          </div>
          <div>
            <FieldLabel>GST number</FieldLabel>
            <Input type="text" name="gst" value={restaurantForm.gst} onChange={handleRestaurantFormChange} />
          </div>
          <div className="md:col-span-2 min-w-0">
            <FieldLabel>Address</FieldLabel>
            <Textarea name="address" value={restaurantForm.address} onChange={handleRestaurantFormChange} rows={3} />
          </div>
          <div className="md:col-span-2">
            <Btn onClick={handleSaveRestaurant}>
              <Edit3 className="w-4 h-4" />
              Save changes
            </Btn>
          </div>
        </div>
      </Card>

      <Card className="max-w-2xl">
        <h3 className="text-lg font-semibold text-white mb-4">Account security</h3>
        <Alert variant="info">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-indigo-300 font-medium">Change login password</p>
              <p className="text-slate-400 text-sm mt-1">Update your portal password to keep your account secure.</p>
            </div>
          </div>
        </Alert>
        <Btn variant="success" className="mt-5" onClick={() => setShowPasswordModal(true)}>
          <Shield className="w-4 h-4" />
          Change password
        </Btn>
      </Card>
    </div>
  );

  const renderBilling = () => {
    const isFirstDevice = devices.length === 0;
    const onTrial = isTrialRegistration(restaurant, devices.length);
    const pricingPlans = getPortalPricingPlans({ restaurant, branches, devices, trialDaysLeft });

    const handleDownloadInvoice = (invoice) => {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;
      let y = 20;

      // Indian currency formatter
      const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return '₹' + new Intl.NumberFormat('en-IN', {
          minimumFractionDigits: 2
        }).format(num);
      };

      // Colors
      const orangeColor = [194, 112, 47];
      const darkGray = [85, 85, 85];
      const lightGray = [128, 128, 128];

      // Logo placeholder (circular background)
      doc.setFillColor(...orangeColor);
      doc.circle(35, 35, 15, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('R', 35, 40, { align: 'center' });

      // Company details (top right)
      doc.setTextColor(...orangeColor);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(invoice.restaurant?.name || 'Restaurant Name', pageWidth - margin, 25, { align: 'right' });

      doc.setTextColor(...lightGray);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const companyDetails = [
        invoice.restaurant?.address || '123, Main Street',
        'Greater Business Area',
        'Mumbai 400001',
        'India'
      ];
      let companyY = 35;
      companyDetails.forEach(detail => {
        doc.text(detail, pageWidth - margin, companyY, { align: 'right' });
        companyY += 5;
      });
      // GSTIN in header if present
      if (invoice.restaurant?.gst) {
        doc.setTextColor(...orangeColor);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`GSTIN: ${invoice.restaurant.gst}`, pageWidth - margin, companyY, { align: 'right' });
        companyY += 7;
      }

      y = 80;

      // TAX INVOICE title
      doc.setTextColor(...orangeColor);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('TAX INVOICE', pageWidth / 2, y, { align: 'center' });
      y += 20;

      // Bill To section
      doc.setTextColor(...darkGray);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Bill To', margin, y);
      doc.setTextColor(...orangeColor);
      doc.setFontSize(11);
      doc.text('Customer Name', margin, y + 10);
      doc.setTextColor(...lightGray);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Customer Address Line 1', margin, y + 18);
      doc.text('City, State', margin, y + 26);
      doc.text('Pincode', margin, y + 34);

      // Invoice details table (top right)
      const tableX = pageWidth - 100;
      const tableY = y;
      const cellWidth = 35;
      const cellHeight = 10;
      const invoiceDetails = [
        ['Invoice#', invoice.invoiceNumber || 'INV-000001'],
        ['Invoice Date', invoice.date ? new Date(invoice.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')],
        ['Terms', 'Due on Receipt'],
        ['Due Date', invoice.date ? new Date(invoice.date).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB')]
      ];
      invoiceDetails.forEach((row, index) => {
        const currentY = tableY + (index * cellHeight);
        doc.setFillColor(...orangeColor);
        doc.rect(tableX, currentY, cellWidth, cellHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text(row[0], tableX + 2, currentY + 7);
        doc.setFillColor(255, 255, 255);
        doc.rect(tableX + cellWidth, currentY, cellWidth, cellHeight, 'FD');
        doc.setTextColor(...darkGray);
        doc.setFont('helvetica', 'normal');
        doc.text(row[1], tableX + cellWidth + 2, currentY + 7);
      });
      y += 60;

      // Ship To section
      doc.setTextColor(...darkGray);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Ship To', margin, y);
      doc.setTextColor(...lightGray);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Same as billing address', margin, y + 10);
      doc.text('City, State', margin, y + 18);
      doc.text('Pincode', margin, y + 26);
      y += 50;

      // Items table
      const itemsTableY = y;
      const colPositions = [margin, margin + 15, margin + 100, margin + 120, margin + 145];
      doc.setFillColor(...orangeColor);
      doc.rect(margin, itemsTableY, pageWidth - 2 * margin, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('#', colPositions[0] + 2, itemsTableY + 8);
      doc.text('Item & Description', colPositions[1] + 2, itemsTableY + 8);
      doc.text('Qty', colPositions[2] + 2, itemsTableY + 8);
      doc.text('Rate', colPositions[3] + 2, itemsTableY + 8);
      doc.text('Amount', colPositions[4] + 2, itemsTableY + 8);
      y = itemsTableY + 12;
      const items = [
        {
          id: 1,
          name: invoice.description || 'Service Charge',
          desc: 'Restaurant service charges',
          qty: 1.00,
          rate: parseFloat(invoice.amount || 0),
          amount: parseFloat(invoice.amount || 0)
        }
      ];
      items.forEach((item, index) => {
        const rowHeight = 25;
        const currentY = y + (index * rowHeight);
        doc.setFillColor(index % 2 === 0 ? 250 : 255, 250, 250);
        doc.rect(margin, currentY, pageWidth - 2 * margin, rowHeight, 'F');
        doc.setDrawColor(230, 230, 230);
        doc.rect(margin, currentY, pageWidth - 2 * margin, rowHeight, 'D');
        doc.setTextColor(...darkGray);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(item.id.toString(), colPositions[0] + 2, currentY + 8);
        doc.setFont('helvetica', 'bold');
        doc.text(item.name, colPositions[1] + 2, currentY + 8);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.text(item.desc, colPositions[1] + 2, currentY + 16);
        doc.setFontSize(10);
        doc.text(item.qty.toFixed(2), colPositions[2] + 2, currentY + 12);
        doc.text(formatCurrency(item.rate), colPositions[3] + 2, currentY + 12);
        doc.text(formatCurrency(item.amount), colPositions[4] + 2, currentY + 12);
      });
      y += items.length * 25 + 20;

      // Summary section
      const summaryX = pageWidth - 80;
      const taxAmount = parseFloat(invoice.tax || 0);
      const baseAmount = parseFloat(invoice.amount || 0);
      const total = baseAmount + taxAmount;
      const summaryData = [
        ['Sub Total', formatCurrency(baseAmount)],
        ['Tax (GST)', `${formatCurrency(taxAmount)}${invoice.restaurant?.gst ? ' (GSTIN: ' + invoice.restaurant.gst + ')' : ''}`],
        ['Total', formatCurrency(total)]
      ];
      summaryData.forEach((row, index) => {
        const currentY = y + (index * 8);
        doc.setTextColor(...darkGray);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(row[0], summaryX, currentY, { align: 'right' });
        doc.text(row[1], summaryX + 40, currentY, { align: 'right' });
      });
      y += 30;

      // Balance Due section
      doc.setFillColor(...orangeColor);
      doc.rect(summaryX - 10, y, 70, 12, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Balance Due', summaryX, y + 8, { align: 'right' });
      doc.text(formatCurrency(total), summaryX + 40, y + 8, { align: 'right' });

      // Thank you message
      doc.setTextColor(...lightGray);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'italic');
      doc.text('Thanks for your business.', margin, y + 30);

      // Terms & Conditions
      doc.setTextColor(...darkGray);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Terms & Conditions', margin, y + 50);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('All payments must be made in full before the', margin, y + 60);
      doc.text('commencement of any service work.', margin, y + 68);

      // Save the PDF
      doc.save(`Invoice_${invoice.invoiceNumber || 'INV-000001'}.pdf`);
    };

    return (
      <div className={dash.page}>
        <PageHeader
          overline="Pricing"
          title="Simple, honest pricing"
          subtitle="Keep free for 7 days after signup. Then ₹1,999 every year per branch — mobile on your LAN stays free."
        />

        {onTrial && (
          <TrialBanner
            trialDaysLeft={trialDaysLeft}
            trialEndsAt={restaurant?.trialEndsAt}
          />
        )}

        <div className="grid w-full min-w-0 grid-cols-1 md:grid-cols-3 gap-4 lg:gap-5 items-stretch">
          {pricingPlans.map((plan) => (
            <PricingPlanCard
              key={plan.id}
              plan={plan}
              onAction={plan.contact || plan.comingSoon ? undefined : openRegistration}
            />
          ))}
        </div>

        <CloudComingSoonAlert />

        <PricingFootnote />

        <Card>
          <h3 className="text-lg font-semibold text-white mb-4">Invoice history</h3>
          {invoices.length === 0 ? (
            <p className="text-slate-400 text-sm">No invoices yet. Invoices appear when you add or renew a branch.</p>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div key={invoice._id || invoice.id} className={cn(dash.nested, 'p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4')}>
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-white">{invoice.invoiceNumber}</p>
                      <p className="text-sm text-slate-400">
                        {invoice.date ? new Date(invoice.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                      </p>
                      <p className="text-xs text-slate-500">{invoice.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-semibold text-white">₹{invoice.amount}</span>
                    <Badge variant={invoice.status === 'Paid' ? 'success' : 'warning'}>{invoice.status}</Badge>
                    <Btn variant="secondary" className="text-sm py-2" onClick={() => handleDownloadInvoice(invoice)}>
                      <Download className="w-4 h-4" />
                      PDF
                    </Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  const renderContact = () => (
    <div className={dash.page}>
      <PageHeader
        overline="Support"
        title="Contact us"
        subtitle="Questions about branches, licensing, or setup? We're here to help."
      />

      <div className={dash.grid2}>
        <Card>
          <h3 className="text-lg font-semibold text-white mb-4">Get support</h3>
          <p className="text-slate-400 text-sm mb-6">For technical support and billing inquiries:</p>
          <div className="space-y-4 mb-6">
            <a href="mailto:support@homebillingpos.com" className="flex items-center gap-3 text-white hover:text-indigo-300 transition-colors">
              <Mail className="w-5 h-5 text-indigo-400 shrink-0" />
              <span>support@homebillingpos.com</span>
            </a>
            <a href="tel:+919867765397" className="flex items-center gap-3 text-white hover:text-indigo-300 transition-colors">
              <Phone className="w-5 h-5 text-indigo-400 shrink-0" />
              <span>+91 98677 65397</span>
            </a>
          </div>
          <Alert variant="info">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-indigo-400 shrink-0" />
              <div>
                <p className="text-indigo-300 font-medium">We typically reply within one business day</p>
                <p className="text-slate-400 text-sm mt-1">Include your restaurant name and a brief description of your question.</p>
              </div>
            </div>
          </Alert>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-white mb-4">Send a message</h3>
          <div className="space-y-4">
            <div>
              <FieldLabel>Your email</FieldLabel>
              <Input type="email" placeholder="you@restaurant.com" defaultValue={restaurant?.email || ''} />
            </div>
            <div>
              <FieldLabel>Subject</FieldLabel>
              <Input type="text" placeholder="Brief description" />
            </div>
            <div>
              <FieldLabel>Message</FieldLabel>
              <Textarea rows={4} placeholder="Describe your question in detail..." />
            </div>
            <Btn className="w-full">Send message</Btn>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'branches':
        return (
          <BranchesPanel
            branches={branches}
            devices={devices}
            restaurant={restaurant}
            onRefresh={fetchBranches}
            onInvoicesRefresh={fetchInvoices}
            onError={setBranchError}
            onClearError={() => setBranchError(null)}
          />
        );
      case 'settings': return renderSettings();
      case 'billing': return renderBilling();
      case 'contact': return renderContact();
      default: return renderOverview();
    }
  };

  const renderRegistrationModal = () => {
    if (!showRegistrationModal) return null;

    const onTrial = isTrialRegistration(restaurant, devices.length);
    const regStepIndex = registrationStep === 'serial' ? 0 : 1;
    const modalAvailableBranches = getBranchesAvailableForCounterPc(branches, devices);
    const selectedBranchSlotUsed = branchHasCounterPcSlotUsed(selectedBranchId, devices, branches);

    const selectedBranch =
      branches.find((b) => b._id === selectedBranchId) ||
      branches.find((b) => b.isDefault) ||
      branches[0];

    const generateLicenseKey = async () => {
      if (!selectedBranch?.syncLicenseKey) {
        setError('Branch sync key not ready. Refresh the dashboard or check Branches.');
        return null;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        return null;
      }

      const res = await fetch(`${process.env.REACT_APP_API_URL}/licenses/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          branchId: selectedBranch._id,
          serial: deviceSerial.trim(),
          name: deviceName.trim(),
          username: deviceUsername.trim(),
          password: devicePassword,
          years: 1,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to generate activation key');
        return null;
      }

      const data = await res.json();
      return data.licenseKey;
    };

    const isUsernameTaken = devices.some(
      (device) => device.username && device.username.toLowerCase() === deviceUsername.trim().toLowerCase()
    );

    const closeRegistration = () => {
      setShowRegistrationModal(false);
      setRegistrationStep('serial');
      setDeviceSerial('');
      setDeviceName('');
      setDeviceUsername('');
      setDevicePassword('');
      setGeneratedLicenseKey('');
    };

    const handleConnectCounterPc = async () => {
      setConnectingCounterPc(true);
      setError(null);
      try {
        const newLicenseKey = await generateLicenseKey();
        const success = await registerDevice(newLicenseKey, 0);
        if (success) {
          setGeneratedLicenseKey(newLicenseKey);
          setRegistrationStep('license');
        }
      } finally {
        setConnectingCounterPc(false);
      }
    };

    return (
      <Modal
        open={showRegistrationModal}
        onClose={closeRegistration}
        title={onTrial ? 'Connect counter PC — free trial' : 'Connect counter PC'}
        wide
      >
          <StepIndicator steps={['Counter PC details', 'Activation key']} current={regStepIndex} />

          {registrationStep === 'serial' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Branch / Location *</label>
                {branches.filter((b) => b.status === 'active').length === 0 ? (
                  <Alert variant="warning">
                    <p className="mb-2">No active branches found.</p>
                    <Btn variant="ghost" onClick={() => { closeRegistration(); handleTabChange('branches'); }}>
                      Go to Branches →
                    </Btn>
                  </Alert>
                ) : modalAvailableBranches.length === 0 ? (
                  <Alert variant="warning">
                    <p className="mb-2">
                      Every branch has already used its one-time counter PC slot. Add another branch (₹1,999/year) for a new shop location.
                    </p>
                    <Btn variant="ghost" onClick={() => { closeRegistration(); handleTabChange('branches'); }}>
                      Add branch →
                    </Btn>
                  </Alert>
                ) : (
                  <>
                    <Select
                      value={selectedBranchId}
                      onChange={(e) => setSelectedBranchId(e.target.value)}
                    >
                      {modalAvailableBranches.map((branch) => (
                        <option key={branch._id} value={branch._id}>
                          {branch.name}
                          {branch.city ? ` — ${branch.city}` : ''}
                          {branch.isDefault ? ' (Default)' : ''}
                        </option>
                      ))}
                    </Select>
                    <p className="text-slate-500 text-xs mt-1">
                      One counter PC per branch, registered once. This cannot be undone — choose the correct location.
                    </p>
                  </>
                )}
              </div>

              <div>
                <FieldLabel>Counter PC ID</FieldLabel>
                <Input
                  type="text"
                  value={deviceSerial}
                  onChange={(e) => setDeviceSerial(e.target.value)}
                  placeholder="Shown in desktop POS on first launch"
                />
              </div>

              <div>
                <FieldLabel>Counter name</FieldLabel>
                <Input
                  type="text"
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  placeholder="e.g., Main Counter POS"
                />
              </div>

              <div>
                <FieldLabel>Desktop login username</FieldLabel>
                <Input
                  type="text"
                  value={deviceUsername}
                  onChange={(e) => setDeviceUsername(e.target.value)}
                  placeholder="Used to sign in on the POS app"
                />
              </div>

              <div>
                <FieldLabel>Desktop login password</FieldLabel>
                <Input
                  type="password"
                  value={devicePassword}
                  onChange={(e) => setDevicePassword(e.target.value)}
                  placeholder="Min. 8 characters"
                />
                <p className="text-slate-500 text-xs mt-1">Use uppercase, lowercase, number, and special character</p>
              </div>

              <div className="bg-indigo-600 bg-opacity-10 border border-indigo-500/40 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-indigo-400 mt-0.5" />
                  <div>
                    <p className="text-indigo-400 font-medium">One counter PC per branch — once only</p>
                    <p className="text-slate-300 text-sm">
                      {onTrial
                        ? `Free during your ${trialDaysLeft}-day trial. After trial: ₹${RENEWAL_PRICE}/year per branch.`
                        : `Each branch gets one counter PC registration, ever. Revoking access does not free the slot — add a branch (₹${RENEWAL_PRICE}/year) for a new location.`}
                    </p>
                  </div>
                </div>
              </div>

              {isUsernameTaken && (
                <p className="text-red-400 text-xs mt-1">This username is already used for another counter PC in your restaurant.</p>
              )}

              {selectedBranchSlotUsed && (
                <p className="text-red-400 text-xs">This branch already used its one-time counter PC slot.</p>
              )}

              <Btn
                className="w-full"
                onClick={handleConnectCounterPc}
                disabled={
                  connectingCounterPc ||
                  modalAvailableBranches.length === 0 ||
                  !selectedBranchId ||
                  selectedBranchSlotUsed ||
                  !deviceSerial.trim() ||
                  !deviceName.trim() ||
                  !deviceUsername.trim() ||
                  !devicePassword.trim() ||
                  devicePassword.length < 8 ||
                  isUsernameTaken
                }
              >
                {connectingCounterPc
                  ? 'Connecting…'
                  : onTrial
                    ? 'Connect & activate trial'
                    : 'Generate activation key'}
              </Btn>
            </div>
          ) : (
            <div
              className="space-y-4"
              style={{
                maxHeight: '70vh',
                minHeight: '300px',
                overflowY: 'auto',
                paddingRight: '2px'
              }}
            >
              <Alert variant="success">
                <div className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-emerald-300 font-semibold">
                      {onTrial ? '7-day free trial activated!' : 'Counter PC connected!'}
                    </p>
                    <p className="text-slate-400 text-sm mt-1">
                      {onTrial
                        ? 'Paste the activation key in your desktop POS app. Full access until trial ends.'
                        : 'Your counter PC is linked to your cloud account. Paste the key in the desktop POS app.'}
                    </p>
                  </div>
                </div>
              </Alert>

              <div className="bg-slate-950/80 rounded-lg p-4 border border-slate-700">
                <h4 className="text-white font-medium mb-3">Counter PC details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Counter name:</span>
                    <span className="text-white">{deviceName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Counter PC ID:</span>
                    <span className="text-white">{deviceSerial}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Username:</span>
                    <span className="text-white">{deviceUsername}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Branch:</span>
                    <span className="text-white">{selectedBranch?.name || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Access:</span>
                    <span className="text-white">
                      {onTrial
                        ? `Trial until ${restaurant?.trialEndsAt ? new Date(restaurant.trialEndsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'trial end'}`
                        : '1 year (branch subscription)'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-slate-950/80 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">Activation key</h4>
                  <button
                    onClick={() => copyLicenseKey(generatedLicenseKey, 'new')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
                  >
                    {copiedKey === 'new' ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
                <p className="text-white text-sm font-mono break-all bg-slate-900 p-3 rounded border border-slate-700">
                  {generatedLicenseKey}
                </p>
                <p className="text-slate-400 text-xs mt-2">
                  Save this activation key securely. You'll need it to sign in on the counter PC.
                </p>
              </div>

              <div className="bg-slate-950/80 rounded-lg p-4 border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-medium">Counter PC login</h4>
                  <button
                    onClick={() => copyLicenseKey(`${deviceUsername}\n${devicePassword}`, 'credentials')}
                    className="inline-flex items-center px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                  >
                    {copiedKey === 'credentials' ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-1" />
                        Copy All
                      </>
                    )}
                  </button>
                </div>
                <div className="space-y-2">
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Username</p>
                    <p className="text-white text-sm font-mono bg-slate-900 p-2 rounded border border-slate-700">{deviceUsername}</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-xs mb-1">Password</p>
                    <p className="text-white text-sm font-mono bg-slate-900 p-2 rounded border border-slate-700">{devicePassword}</p>
                  </div>
                </div>
                <p className="text-slate-400 text-xs mt-3">
                  Save these credentials securely. You'll need them to log into your device.
                </p>
              </div>

              <Btn className="w-full" onClick={closeRegistration}>
                Done
              </Btn>
            </div>
          )}
      </Modal>
    );
  };

  const renderPasswordModal = () => {
    const handlePasswordChange = async () => {
      setPasswordError('');
      setPasswordChangeMsg('');
      if (!currentPassword.trim()) {
        setPasswordError('Current password is required');
        return;
      }
      if (!newPassword.trim()) {
        setPasswordError('New password is required');
        return;
      }
      if (newPassword.length < 8) {
        setPasswordError('New password must be at least 8 characters long');
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordError('New passwords do not match');
        return;
      }
      if (newPassword === currentPassword) {
        setPasswordError('New password must be different from current password');
        return;
      }
      if (!userId) {
        setPasswordError('User ID not found. Please re-login.');
        return;
      }
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ password: newPassword })
        });
        if (!res.ok) throw new Error('Failed to change password');
        setPasswordChangeMsg('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
        setTimeout(() => setShowPasswordModal(false), 1200);
      } catch (err) {
        setPasswordError('Failed to change password');
      }
    };

    const closePasswordModal = () => {
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    };

    return (
      <Modal open={showPasswordModal} onClose={closePasswordModal} title="Change password">
          <div className="space-y-4">
            <div>
              <FieldLabel>Current password</FieldLabel>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your current password"
              />
            </div>

            <div>
              <FieldLabel>New password</FieldLabel>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
              />
              <p className="text-slate-500 text-xs mt-1">Min. 8 characters with mixed case, number, and symbol</p>
            </div>

            <div>
              <FieldLabel>Confirm new password</FieldLabel>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
              />
            </div>

            {passwordError && <Alert variant="error">{passwordError}</Alert>}
            {passwordChangeMsg && <Alert variant="success">{passwordChangeMsg}</Alert>}

            <Alert variant="info">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
                <p className="text-slate-400 text-sm">Use a strong, unique password. A password manager is recommended.</p>
              </div>
            </Alert>

            <div className="flex gap-3">
              <Btn variant="secondary" className="flex-1" onClick={closePasswordModal}>
                Cancel
              </Btn>
              <Btn
                variant="success"
                className="flex-1"
                onClick={handlePasswordChange}
                disabled={!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()}
              >
                Change password
              </Btn>
            </div>
          </div>
      </Modal>
    );
  };

  // Handler for restaurant info form
  const handleRestaurantFormChange = (e) => {
    setRestaurantForm({ ...restaurantForm, [e.target.name]: e.target.value });
  };

  const handleSaveRestaurant = async () => {
    setRestaurantSaveMsg('');
    if (!restaurant?._id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/restaurants/${restaurant._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(restaurantForm)
      });
      if (!res.ok) throw new Error('Failed to update restaurant');
      const data = await res.json();
      setRestaurant(data);
      setRestaurantSaveMsg('Restaurant details updated successfully!');
    } catch (err) {
      setRestaurantSaveMsg('Failed to update restaurant');
    }
  };

  return (
    <div className={dash.shell}>
      <div className={dash.shellRow}>
        {sidebarOpen && (
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-40 bg-slate-950/60 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {renderSidebar(
          cn(
            dash.sidebarMobile,
            'lg:hidden fixed z-50 transition-transform duration-200',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          ),
          true,
        )}
        {renderSidebar(cn(dash.sidebar, 'hidden lg:block shrink-0'))}

        <div className={dash.main}>
          <div className={dash.topBar}>
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 min-w-0">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 lg:hidden shrink-0"
                >
                  <Menu className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                    <p className={dash.overline}>Dashboard</p>
                  <p className="text-white font-semibold truncate">{activeNavLabel}</p>
                </div>
              </div>
              {activeTab !== 'billing' && (
                canAddCounterPc ? (
                  <Btn className="shrink-0" onClick={openRegistration}>
                    <ArrowUpRight className="w-4 h-4" />
                    <span className="hidden sm:inline">{trialFirstDevice ? 'Start free trial' : 'Connect counter PC'}</span>
                    <span className="sm:hidden">{trialFirstDevice ? 'Trial' : 'Connect PC'}</span>
                  </Btn>
                ) : (
                  <Btn className="shrink-0" variant="secondary" onClick={() => handleTabChange('branches')}>
                    <Building2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Add branch</span>
                    <span className="sm:hidden">Branch</span>
                  </Btn>
                )
              )}
            </div>
          </div>

          <div className={dash.mainScroll}>
            <div className={dash.mainInner}>
              {loading && (
                <div className="flex items-center gap-3 text-slate-400 mb-6">
                  <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  Loading your restaurant...
                </div>
              )}
              {error && <div className="mb-4"><Alert variant="error">{error}</Alert></div>}
              {branchError && activeTab === 'branches' && (
                <div className="mb-4"><Alert variant="error">{branchError}</Alert></div>
              )}
              {!loading && renderContent()}
            </div>
          </div>

          {renderRegistrationModal()}
          {renderPasswordModal()}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;